import React, { useState, useEffect, useCallback, useRef } from "react";
import { safeUrl } from "../../utils/safeUrl";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import toast from "react-hot-toast";
import {
  FiArrowLeft,
  FiGift,
  FiCalendar,
  FiUsers,
  FiAward,
  FiCheckCircle,
  FiAlertTriangle,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiRefreshCw,
  FiPlay,
  FiDollarSign,
  FiShield,
  FiSearch,
  FiSliders,
  FiExternalLink,
  FiCheck,
  FiCopy,
  FiInfo,
  FiClock,
  FiX,
  FiCreditCard,
  FiActivity,
  FiCheckSquare,
  FiRepeat,
  FiPieChart,
  FiBarChart2,
} from "react-icons/fi";

import {
  fetchEventDetails,
  deleteEvent,
  publishEvent,
  fetchEventSponsors,
  addEventSponsor,
  updateEventSponsor,
  deleteEventSponsor,
  fetchEventPrizes,
  createEventPrize,
  updateEventPrize,
  deleteEventPrize,
  fetchEventParticipants,
  adjustParticipantPoints,
  blockEventUser,
  unblockEventUser,
  recomputeEventRanks,
  finalizeEvent,
  fetchEventWinners,
  markWinnerUsdtPaid,
  fetchEventReconciliation,
} from "../../utils/eventApi";
import {
  rewardsOf,
  buildRewards,
  rewardsToText,
  totalRewards,
  normaliseCurrency,
  isAutoCredited,
  manualRewardsOf,
  EMPTY_PRIZE_TEXT,
  COIN_CURRENCIES,
} from "../../utils/eventRewards";
import CurrencySelect from "./components/CurrencySelect";

import SponsorModal from "./components/SponsorModal";
import AdjustPointsModal from "./components/AdjustPointsModal";
import BlockUserModal from "./components/BlockUserModal";
import ParticipantHistoryDrawer from "./components/ParticipantHistoryDrawer";
import PointsLedgerTab from "./components/PointsLedgerTab";
import TaskActivityTab from "./components/TaskActivityTab";
import PointsSummaryTab from "./components/PointsSummaryTab";
import AnalyticsTab from "./components/AnalyticsTab";
import ExchangeRatesTab from "./components/ExchangeRatesTab";
import ConfirmActionModal from "./components/ConfirmActionModal";
import ApprovalQueue from "./components/ApprovalQueue";
import ActivityLog from "./components/ActivityLog";
import { getStatusBadgeClass, getStatusLabel } from "./EventsList";
import { usePermission } from "../../context/PermissionContext";

const getUserId = (row) => row?.user_id ?? row?.userId ?? row?.userID ?? row?.id ?? null;

const TABS = [
  { key: "overview", label: "Overview", icon: FiInfo },
  { key: "analytics", label: "Analytics", icon: FiBarChart2 },
  { key: "sponsors", label: "Sponsors", icon: FiShield },
  { key: "prizes", label: "Prize Ladder", icon: FiAward },
  { key: "points-summary", label: "Points Summary", icon: FiPieChart },
  { key: "participants", label: "Participants & Points", icon: FiUsers },
  { key: "ledger", label: "Points Ledger", icon: FiActivity },
  { key: "task-activity", label: "Task Activity", icon: FiCheckSquare },
  { key: "exchange", label: "Exchange", icon: FiRepeat },
  { key: "winners", label: "Winners & Payouts", icon: FiDollarSign },
  { key: "approval", label: "Reward Coin Approval", icon: FiCreditCard },
  { key: "audit", label: "Reconcile / Audit", icon: FiCheckCircle },
  { key: "activity", label: "Activity Log", icon: FiActivity },
];

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // A participant's name links to their user page only for staff who can
  // open it — otherwise the link would land on Access Denied.
  const { hasPermission } = usePermission();
  const canViewUsers = hasPermission("users", "view");

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  // The tab lives in the URL so the list can link straight to a specific one
  // — "view participants" should land on participants, not on the overview with
  // another click still to make.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    TABS.some((t) => t.key === requestedTab) ? requestedTab : "overview"
  );

  const openTab = (key) => {
    setActiveTab(key);
    setSearchParams(key === "overview" ? {} : { tab: key }, { replace: true });
  };

  // Sub-resources
  const [sponsors, setSponsors] = useState([]);
  const [sponsorsLoading, setSponsorsLoading] = useState(false);

  const [prizes, setPrizes] = useState([]);
  const [prizesLoading, setPrizesLoading] = useState(false);

  const [participants, setParticipants] = useState([]);
  // Read-only points history. Separate from the adjust modal on purpose: one
  // explains what happened, the other changes it.
  const [historyUser, setHistoryUser] = useState(null);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [participantSearch, setParticipantSearch] = useState("");
  const [debouncedParticipantSearch, setDebouncedParticipantSearch] = useState("");
  const [participantPage, setParticipantPage] = useState(1);
  const [participantTotalPages, setParticipantTotalPages] = useState(1);
  const [participantTotal, setParticipantTotal] = useState(0);
  // "" = everyone, "false" = active only, "true" = blocked only
  const [participantBlockFilter, setParticipantBlockFilter] = useState("");
  // The participant the block/unblock modal is open for.
  const [blockTarget, setBlockTarget] = useState(null);

  const [winners, setWinners] = useState([]);
  const [winnersLoading, setWinnersLoading] = useState(false);
  const [winnerPrizeStatus, setWinnerPrizeStatus] = useState("all");
  const [winnerPage, setWinnerPage] = useState(1);
  const [winnerTotalPages, setWinnerTotalPages] = useState(1);

  const [winnerTotal, setWinnerTotal] = useState(0);

  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  // Modals state
  const [isSponsorModalOpen, setIsSponsorModalOpen] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);

  const [isAdjustModalOpen, setIsAdjustModalOpen] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    subMessage: "",
    confirmText: "",
    confirmColor: "blue",
    icon: null,
    action: null,
    loading: false,
  });

  const participantSearchTimer = useRef(null);
  const autoSaveTimers = useRef({});

  /**
   * Normalise a tier from the API into what the editor holds.
   *
   * `rewards` is the whole prize now, so the editor keeps that one list and
   * nothing else: PX is edited as its own field because it is the currency the
   * API credits automatically, and every other currency is a row underneath.
   * Tiers saved before the API had `rewards` still arrive with only
   * `reward_px` / `reward_usdt`, which `rewardsOf` reads instead.
   */
  const toEditablePrize = (prize) => ({ ...prize, rewards: rewardsOf(prize) });

  // Load Main Event Details
  const loadEventDetails = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchEventDetails(id);
      if (res && res.status) {
        setEvent(res.data);
        if (res.data?.sponsors) setSponsors(res.data.sponsors);
        if (res.data?.prizes) {
          setPrizes((res.data.prizes || []).map(toEditablePrize));
        }
      } else {
        toast.error(res?.message || "Failed to load event details");
      }
    } catch (err) {
      toast.error(err.message || "Network error loading event");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadEventDetails();
  }, [loadEventDetails]);

  // Load Sponsors
  const loadSponsors = useCallback(async () => {
    setSponsorsLoading(true);
    try {
      const res = await fetchEventSponsors(id);
      if (res && res.status) {
        setSponsors(res.data || []);
      }
    } catch (err) {
      toast.error("Failed to load sponsors");
    } finally {
      setSponsorsLoading(false);
    }
  }, [id]);

  // Load Prizes
  const loadPrizes = useCallback(async () => {
    setPrizesLoading(true);
    try {
      const res = await fetchEventPrizes(id);
      if (res && res.status) {
        setPrizes((res.data || []).map(toEditablePrize));
      }
    } catch (err) {
      toast.error("Failed to load prizes");
    } finally {
      setPrizesLoading(false);
    }
  }, [id]);

  // Load Participants
  const loadParticipants = useCallback(async () => {
    setParticipantsLoading(true);
    try {
      const res = await fetchEventParticipants(id, {
        page: participantPage,
        limit: 25,
        search: debouncedParticipantSearch,
        isBlocked: participantBlockFilter,
      });
      if (res && res.status) {
        setParticipants(res.data || []);
        if (res.pagination) {
          setParticipantPage(res.pagination.current_page || 1);
          setParticipantTotalPages(res.pagination.total_pages || 1);
          setParticipantTotal(res.pagination.total || 0);
        }
      }
    } catch (err) {
      toast.error("Failed to load participants");
    } finally {
      setParticipantsLoading(false);
    }
  }, [id, participantPage, debouncedParticipantSearch, participantBlockFilter]);

  // Load Winners

  const loadWinners = useCallback(async () => {
    setWinnersLoading(true);
    try {
      const res = await fetchEventWinners(id, {
        page: winnerPage,
        limit: 50,
        prize_status: winnerPrizeStatus,
      });
      if (res && res.status) {
        setWinners(res.data || []);
        if (res.pagination) {
          setWinnerPage(res.pagination.current_page || 1);
          setWinnerTotalPages(res.pagination.total_pages || 1);
          setWinnerTotal(res.pagination.total || 0);
        }
      }
    } catch (err) {
      toast.error("Failed to load winners queue");
    } finally {
      setWinnersLoading(false);
    }
  }, [id, winnerPage, winnerPrizeStatus]);

  // Trigger loading when tab changes
  useEffect(() => {
    if (activeTab === "sponsors") loadSponsors();
    if (activeTab === "prizes") loadPrizes();
    if (activeTab === "participants") loadParticipants();
    if (activeTab === "winners") loadWinners();
    // The approval tab loads itself — ApprovalQueue owns that fetch.
  }, [activeTab, loadSponsors, loadPrizes, loadParticipants, loadWinners]);

  // Participant search debounce
  const handleParticipantSearchChange = (e) => {
    const val = e.target.value;
    setParticipantSearch(val);
    setParticipantPage(1);
    clearTimeout(participantSearchTimer.current);
    participantSearchTimer.current = setTimeout(() => {
      setDebouncedParticipantSearch(val);
    }, 400);
  };

  // Reconcile Audit
  const handleRunAudit = async () => {
    setAuditLoading(true);
    try {
      const res = await fetchEventReconciliation(id);
      if (res && res.status) {
        setAuditData(res.data || {});
        toast.success(res.message || "Audit completed!");
      }
    } catch (err) {
      toast.error(err.message || "Failed to run audit");
    } finally {
      setAuditLoading(false);
    }
  };

  // Publish Event
  const handlePublish = () => {
    // Publishing does one of two things and the API decides which from the
    // start date, so the confirmation has to say which one is about to happen.
    const startsLater = new Date(event?.start_date).getTime() > Date.now();

    setConfirmModal({
      isOpen: true,
      title: startsLater ? "Announce Event" : "Publish Event",
      message: startsLater
        ? `Announce "${event?.name}" as coming soon?`
        : `Publish "${event?.name}" and take it live?`,
      subMessage: startsLater
        ? "Users will see the event and its start date, but check-in and scoring stay off until it opens on its own. Only one event can be queued at a time."
        : "Only one event can run at a time. Participants will start accumulating points immediately.",
      confirmText: startsLater ? "Announce" : "Publish Now",
      confirmColor: "emerald",
      icon: FiPlay,
      action: async () => {
        try {
          setConfirmModal((prev) => ({ ...prev, loading: true }));
          const res = await publishEvent(id, event?.notify_on_publish);
          toast.success(res.message || "Event is now live!");
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
          loadEventDetails();
        } catch (err) {
          toast.error(err.message || "Failed to publish event");
        } finally {
          setConfirmModal((prev) => ({ ...prev, loading: false }));
        }
      },
    });
  };

  // Delete Event
  const handleDeleteEvent = () => {
    setConfirmModal({
      isOpen: true,
      title: "Delete Event",
      message: `Are you sure you want to permanently delete "${event?.name}"?`,
      subMessage: "Only draft events can be deleted. This operation is irreversible.",
      confirmText: "Delete Event",
      confirmColor: "red",
      icon: FiTrash2,
      action: async () => {
        try {
          setConfirmModal((prev) => ({ ...prev, loading: true }));
          const res = await deleteEvent(id);
          toast.success(res.message || "Event deleted successfully");
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
          navigate("/event");
        } catch (err) {
          toast.error(err.message || "Failed to delete event");
        } finally {
          setConfirmModal((prev) => ({ ...prev, loading: false }));
        }
      },
    });
  };

  // Recompute Ranks
  const handleRecomputeRanks = async () => {
    try {
      toast.loading("Recomputing leaderboard ranks...", { id: "recompute" });
      const res = await recomputeEventRanks(id);
      toast.success(res.message || `Ranks recomputed! (${res.data?.took_ms || 0}ms)`, { id: "recompute" });
      loadEventDetails();
      if (activeTab === "participants") loadParticipants();
    } catch (err) {
      toast.error(err.message || "Failed to recompute ranks", { id: "recompute" });
    }
  };

  // Finalize Event
  const handleFinalizeEvent = () => {
    setConfirmModal({
      isOpen: true,
      title: "Finalize Event & Freeze Standings",
      message: `Are you sure you want to finalize "${event?.name}"?`,
      subMessage:
        "CRITICAL: Finalizing closes the event, permanently freezes final leaderboard ranks, automatically credits all PX token prizes directly to winners, and populates the USDT payout queue.",
      confirmText: "Finalize & Credit PX",
      confirmColor: "blue",
      icon: FiAward,
      action: async () => {
        try {
          setConfirmModal((prev) => ({ ...prev, loading: true }));
          const res = await finalizeEvent(id);
          toast.success(res.message || "Event finalized successfully!");
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
          loadEventDetails();
          openTab("winners");
        } catch (err) {
          toast.error(err.message || "Failed to finalize event");
        } finally {
          setConfirmModal((prev) => ({ ...prev, loading: false }));
        }
      },
    });
  };

  // Mark the hand-transferred part of a prize as paid
  const handleMarkPaid = (winner) => {
    const winnerUserId = getUserId(winner);
    // PX is already in their balance; what a person still owes is everything
    // else. Naming USDT here would be wrong for a prize paid in BTC.
    const owed = rewardsToText(manualRewardsOf(winner)) || "the prize";
    setConfirmModal({
      isOpen: true,
      title: "Mark Prize as Paid",
      message: `Confirm that ${owed} was sent to ${winner.username} (${winner.zgram_id || winner.email})?`,
      subMessage: "This will update the payout status to 'paid' and record the payment timestamp.",
      confirmText: "Mark as Paid",
      confirmColor: "emerald",
      icon: FiCheckCircle,
      action: async () => {
        try {
          setConfirmModal((prev) => ({ ...prev, loading: true }));
          const res = await markWinnerUsdtPaid(id, winnerUserId);
          toast.success(res.message || "Prize marked as paid!");
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
          loadWinners();
        } catch (err) {
          toast.error(err.message || "Failed to mark as paid");
        } finally {
          setConfirmModal((prev) => ({ ...prev, loading: false }));
        }
      },
    });
  };

  // Sponsor Handlers
  const handleSaveSponsor = async (payload, sponsorId, isMultipart) => {
    if (sponsorId) {
      await updateEventSponsor(id, sponsorId, payload, isMultipart);
      toast.success("Sponsor updated successfully!");
    } else {
      await addEventSponsor(id, payload, isMultipart);
      toast.success("Sponsor added successfully!");
    }
    loadSponsors();
  };

  const handleDeleteSponsor = (sponsor) => {
    setConfirmModal({
      isOpen: true,
      title: "Remove Sponsor",
      message: `Remove "${sponsor.name}" from event sponsors?`,
      confirmText: "Remove Sponsor",
      confirmColor: "red",
      icon: FiTrash2,
      action: async () => {
        try {
          setConfirmModal((prev) => ({ ...prev, loading: true }));
          await deleteEventSponsor(id, sponsor.id);
          toast.success("Sponsor removed");
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
          loadSponsors();
        } catch (err) {
          toast.error(err.message || "Failed to remove sponsor");
        } finally {
          setConfirmModal((prev) => ({ ...prev, loading: false }));
        }
      },
    });
  };

  // ==============================================================
  // INLINE PRIZE LADDER HANDLERS (AUTOMATIC SAVE - NO BUTTON NEEDED!)
  // ==============================================================

  /**
   * What a tier edit sends.
   *
   * `rewards` is the whole prize: the API derives `reward_px`, `reward_usdt`
   * and the display text from that one list, so sending those separately could
   * only let them disagree with what is on screen.
   *
   * A tier with nothing in it is the exception. `prize_text` is NOT NULL on the
   * API side and cannot be derived from an empty list, so an empty tier has to
   * say so in words.
   */
  const prizePayload = (prize) => {
    const rankFrom = Number(prize.rank_from) || 1;
    const rankTo = Number(prize.rank_to) || rankFrom;
    const { rewards } = buildRewards(prize.rewards);

    return {
      rank_from: rankFrom,
      rank_to: rankTo,
      rank_label: `#${rankFrom}${rankTo > rankFrom ? ` - #${rankTo}` : ""}`,
      rewards,
      ...(rewards.length === 0 ? { prize_text: EMPTY_PRIZE_TEXT } : {}),
      sort_order: prize.sort_order || 1,
    };
  };

  // Quick Add Tier: Always starts after highest rank_to, never alters upper tiers!
  const handleQuickAddPrize = async () => {
    try {
      const maxRankTo = prizes.reduce((max, p) => {
        const toVal = Number(p.rank_to || p.rank_from || 0);
        return Math.max(max, toVal);
      }, 0);

      const nextFrom = maxRankTo > 0 ? maxRankTo + 1 : 1;
      const nextTo = nextFrom;

      const newTierPayload = {
        ...prizePayload({ rank_from: nextFrom, rank_to: nextTo, rewards: [] }),
        sort_order: prizes.length + 1,
      };

      await createEventPrize(id, newTierPayload);
      toast.success(`Prize tier #${nextFrom} added!`);
      loadPrizes();
    } catch (err) {
      toast.error(err.message || "Failed to add prize tier");
    }
  };

  // Automatic saving on field change (with debounce)
  const handleUpdatePrizeAndAutoSave = (prizeId, fieldUpdates) => {
    setPrizes((prev) => {
      return prev.map((p) => {
        if (p.id !== prizeId) return p;
        const updated = { ...p, ...fieldUpdates };

        // Debounced API synchronization (500ms)
        if (autoSaveTimers.current[prizeId]) {
          clearTimeout(autoSaveTimers.current[prizeId]);
        }

        autoSaveTimers.current[prizeId] = setTimeout(async () => {
          try {
            await updateEventPrize(id, prizeId, prizePayload(updated));
          } catch (err) {
            console.error("Auto-save prize tier failed:", err);
          }
        }, 500);

        return updated;
      });
    });
  };

  // ── the reward rows inside one tier ──────────────────────────────────────
  //
  // PX gets its own field because it is the one currency finalize credits by
  // itself; everything else is a row the team transfers by hand. Both live in
  // the same `rewards` list, so the editor keeps PX first and addresses the
  // rest by position. Amounts stay as typed — `buildRewards` drops the blank
  // and half-typed ones when the tier is sent.

  const pxAmountOf = (rewards) => {
    const row = (rewards || []).find((entry) => normaliseCurrency(entry.currency) === "PX");
    return row ? row.amount : "";
  };

  const coinRowsOf = (rewards) =>
    (rewards || []).filter((entry) => normaliseCurrency(entry.currency) !== "PX");

  const composeRewards = (pxAmount, coinRows) => [
    ...(String(pxAmount ?? "").trim() === "" ? [] : [{ currency: "PX", amount: pxAmount }]),
    ...coinRows,
  ];

  const setPrizeRewards = (prize, rewards) =>
    handleUpdatePrizeAndAutoSave(prize.id, { rewards });

  const handlePxAmountChange = (prize, value) =>
    setPrizeRewards(prize, composeRewards(value, coinRowsOf(prize.rewards)));

  const handleCoinRowChange = (prize, index, patch) =>
    setPrizeRewards(
      prize,
      composeRewards(
        pxAmountOf(prize.rewards),
        coinRowsOf(prize.rewards).map((row, i) => (i === index ? { ...row, ...patch } : row))
      )
    );

  const handleAddCoinRow = (prize) =>
    setPrizeRewards(
      prize,
      composeRewards(pxAmountOf(prize.rewards), [
        ...coinRowsOf(prize.rewards),
        { currency: "USDT", amount: "" },
      ])
    );

  const handleRemoveCoinRow = (prize, index) => {
    const rewards = composeRewards(
      pxAmountOf(prize.rewards),
      coinRowsOf(prize.rewards).filter((_, i) => i !== index)
    );
    setPrizeRewards(prize, rewards);
    // Removing a row is a decision, not a keystroke — save it now rather than
    // leaving it to a blur that a click outside the card may never fire.
    handlePrizeBlur({ ...prize, rewards });
  };

  // Immediate save on blur
  const handlePrizeBlur = async (prize) => {
    if (autoSaveTimers.current[prize.id]) {
      clearTimeout(autoSaveTimers.current[prize.id]);
    }
    try {
      await updateEventPrize(id, prize.id, prizePayload(prize));
    } catch (err) {
      console.error("Prize blur save failed:", err);
    }
  };

  const handleDeletePrize = (prize) => {
    setConfirmModal({
      isOpen: true,
      title: "Delete Prize Tier",
      message: `Delete prize tier "${prize.rank_label || `#${prize.rank_from}`}"?`,
      confirmText: "Delete Tier",
      confirmColor: "red",
      icon: FiTrash2,
      action: async () => {
        try {
          setConfirmModal((prev) => ({ ...prev, loading: true }));
          await deleteEventPrize(id, prize.id);
          toast.success("Prize tier removed");
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
          loadPrizes();
        } catch (err) {
          toast.error(err.message || "Failed to remove prize tier");
        } finally {
          setConfirmModal((prev) => ({ ...prev, loading: false }));
        }
      },
    });
  };

  // Manual Point Adjustment
  const handleAdjustPoints = async (payload) => {
    await adjustParticipantPoints(id, payload);
    loadParticipants();
    loadEventDetails();
  };

  // Per-event Block / Unblock — the server re-ranks straight away, so a reload
  // shows the new standings.
  const handleBlockToggle = async ({ user_id, reason, block, held_points_action }) => {
    await (block ? blockEventUser : unblockEventUser)(id, { user_id, reason, held_points_action });
    loadParticipants();
    loadEventDetails();
  };

  // Copy Helper
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      // UTC everywhere, on a 24h clock — the admin reads schedules in the same
      // zone the API stores them, with no mental conversion in between.
      return (
        d.toLocaleDateString("en-US", {
          timeZone: "UTC",
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }) + " UTC"
      );
    } catch {
      return dateStr;
    }
  };

  if (loading && !event) {
    return (
      <div className="flex h-screen bg-gray-50/50">
        <Sidebar />
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <Navbar />
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <FiRefreshCw className="animate-spin text-blue-600" size={24} />
              <p className="text-xs text-gray-500 font-medium">Loading event details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex h-screen bg-gray-50/50">
        <Sidebar />
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <Navbar />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <p className="text-gray-700 font-medium">Event not found</p>
              <button
                onClick={() => navigate("/event")}
                className="mt-3 px-4 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg"
              >
                Back to Events List
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // One total per currency: a prize pool of PX, USDT and BTC has three numbers
  // in it, and adding them together would state something untrue.
  const prizeTotals = totalRewards(prizes);

  return (
    <div className="flex h-screen bg-gray-50/50">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Navbar />

        <main className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Top Navigation & Actions Bar */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate("/event")}
                  className="p-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 rounded-xl transition shadow-sm"
                  title="Back to Events"
                >
                  <FiArrowLeft size={16} />
                </button>
                <div>
                  <div className="flex items-center gap-2.5">
                    <h1 className="text-xl font-bold text-gray-800">{event.name}</h1>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadgeClass(
                        event.status
                      )}`}
                    >
                      {getStatusLabel(event.status)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 font-mono mt-0.5">
                    Event Code: <span className="text-gray-700 font-semibold">{event.event_code}</span>
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={loadEventDetails}
                  title="Refresh Event Data"
                  className="p-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 rounded-xl transition shadow-sm"
                >
                  <FiRefreshCw size={15} className={loading ? "animate-spin text-blue-600" : ""} />
                </button>

                {/* Draft Status Actions */}
                {event.status === "draft" && (
                  <>
                    <button
                      onClick={handlePublish}
                      className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-xl transition shadow-sm shadow-emerald-600/20 flex items-center gap-1.5"
                    >
                      <FiPlay size={14} />{" "}
                      {new Date(event.start_date).getTime() > Date.now()
                        ? "Publish (Announce)"
                        : "Publish (Take Live)"}
                    </button>
                    <button
                      onClick={handleDeleteEvent}
                      className="px-3.5 py-2 bg-rose-50 hover:bg-rose-100 text-rose-600 text-xs font-semibold rounded-xl transition border border-rose-200 flex items-center gap-1.5"
                    >
                      <FiTrash2 size={14} /> Delete
                    </button>
                  </>
                )}

                {/* Announced but not open yet. There is no button here on
                    purpose: the event opens by itself on its start date, and it
                    cannot be deleted or finalized in this state. */}
                {event.status === "coming_soon" && (
                  <span className="px-3.5 py-2 bg-violet-50 text-violet-700 text-xs font-semibold rounded-xl border border-violet-200 flex items-center gap-1.5">
                    <FiClock size={14} /> Opens {formatDate(event.start_date)}
                  </span>
                )}

                {/* Running Status Actions */}
                {event.status === "running" && (
                  <>
                    <button
                      onClick={handleRecomputeRanks}
                      title="Trigger immediate re-ranking"
                      className="px-3.5 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-xl transition border border-indigo-200 flex items-center gap-1.5"
                    >
                      <FiSliders size={14} /> Recompute Ranks
                    </button>
                    <button
                      onClick={handleFinalizeEvent}
                      className="px-3.5 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-xl transition shadow-sm shadow-purple-600/20 flex items-center gap-1.5"
                    >
                      <FiAward size={14} /> Finalize Event
                    </button>
                  </>
                )}

                {/* Edit Event Button (if not completed) */}
                {event.status !== "completed" && (
                  <button
                    onClick={() => navigate(`/event/${id}/edit`)}
                    className="px-3.5 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-semibold rounded-xl transition shadow-sm flex items-center gap-1.5"
                  >
                    <FiEdit2 size={14} /> Edit Event
                  </button>
                )}
              </div>
            </div>

            {/* Summary Stat Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3.5">
              <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between">
                <div className="flex items-center justify-between text-gray-400 mb-2">
                  <span className="text-xs font-medium">Participants</span>
                  <FiUsers className="text-blue-500" size={16} />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-800">
                    {(event.total_participants ?? event.participant_count ?? 0).toLocaleString()}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    Active scorers
                    {event.blocked_count > 0 && (
                      <button
                        onClick={() => {
                          setParticipantBlockFilter("true");
                          setParticipantPage(1);
                          openTab("participants");
                        }}
                        className="ml-1 text-red-500 hover:underline"
                        title="Show blocked participants"
                      >
                        · {Number(event.blocked_count).toLocaleString()} blocked
                      </button>
                    )}
                  </p>
                </div>
              </div>

              <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between">
                <div className="flex items-center justify-between text-gray-400 mb-2">
                  <span className="text-xs font-medium">Timeline Progress</span>
                  <FiCalendar className="text-emerald-500" size={16} />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-800">
                    {event.current_day_number ? `Day ${event.current_day_number}` : "—"}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    {event.total_days ? `${event.total_days} total days` : "Duration unset"}
                  </p>
                </div>
              </div>

              <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between">
                <div className="flex items-center justify-between text-gray-400 mb-2">
                  <span className="text-xs font-medium">Daily Check-in</span>
                  <FiGift className="text-amber-500" size={16} />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-800">+{event.daily_check_in_reward ?? 1} pts</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    {event.check_in_credits_coin ? "Coin Credit ON" : "Points Only"}
                  </p>
                </div>
              </div>

              <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between">
                <div className="flex items-center justify-between text-gray-400 mb-2">
                  <span className="text-xs font-medium">Prize Winners</span>
                  <FiAward className="text-purple-500" size={16} />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-800">Top {event.prize_top_n || 1000}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    {prizes.length} tier{prizes.length === 1 ? "" : "s"} defined
                  </p>
                </div>
              </div>

              <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between col-span-2 sm:col-span-1">
                <div className="flex items-center justify-between text-gray-400 mb-2">
                  <span className="text-xs font-medium">Rank Status</span>
                  <FiClock className="text-indigo-500" size={16} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-800 truncate">
                    {event.finalized_at
                      ? "Finalized & Frozen"
                      : event.ranked_at
                        ? formatDate(event.ranked_at)
                        : "Pending Run"}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    {event.finalized_at ? "Standings locked" : "5-min cron / on demand"}
                  </p>
                </div>
              </div>
            </div>

            {/* How this event scores, and who may win — as set in the form. */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                <p className="text-xs font-semibold text-gray-700 mb-3">Event Points per Activity</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                  {[
                    ["Daily Mining", "1 PX", event.mining_points_per_px ?? 1],
                    ["Streak Bonus", "1 PX", event.streak_points_per_px ?? 1],
                    ["Referral Mining", "1 PX", event.referral_points_per_px ?? 1],
                    ["Referral Join", "1 member", event.referral_join_points ?? 0],
                    ["Daily Check-in", "1 check-in", event.daily_check_in_reward ?? 1],
                    ["Content Approved", "1 content", event.creator_content_points ?? 0],
                    ["Creator Bonus Claim", "1 PX", event.creator_bonus_points_per_px ?? 1],
                  ].map(([label, unit, pts]) => (
                    <div key={label} className="flex items-center justify-between text-[11px]">
                      <span className="text-gray-500">{label}</span>
                      <span className={`font-semibold ${Number(pts) > 0 ? "text-gray-800" : "text-gray-300"}`}>
                        {unit} = {Number(pts)} pts
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                <p className="text-xs font-semibold text-gray-700 mb-3">
                  Eligible Point Activities
                  {event.eligibility_is_default && (
                    <span className="ml-1.5 font-normal text-gray-400">· default, follows the point rates</span>
                  )}
                </p>
                {(event.eligibility || []).length === 0 ? (
                  <p className="text-[11px] text-gray-400">None set</p>
                ) : (
                  <ol className="space-y-2">
                    {event.eligibility.map((row, i) => (
                      <li key={i} className={`flex gap-2 text-[11px] ${row.is_active === false ? "opacity-40" : ""}`}>
                        <span className="w-5 h-5 shrink-0 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold">
                          {i + 1}
                        </span>
                        <span className="min-w-0">
                          <span className="block font-semibold text-gray-800">
                            {row.title}
                            {row.is_active === false && <span className="ml-1 font-normal text-gray-400">(hidden)</span>}
                          </span>
                          {row.description && (
                            <span className="block text-gray-500 whitespace-pre-line">{row.description}</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="flex items-center border-b border-gray-100 overflow-x-auto px-4 gap-2 scrollbar-none">
                {TABS.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => openTab(tab.key)}
                      className={`flex items-center gap-2 py-3.5 px-3.5 text-xs font-medium border-b-2 transition whitespace-nowrap ${isActive
                        ? "border-blue-600 text-blue-600 font-semibold"
                        : "border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-200"
                        }`}
                    >
                      <Icon size={14} className={isActive ? "text-blue-600" : "text-gray-400"} />
                      {tab.label}
                      {tab.key === "sponsors" && sponsors.length > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] bg-gray-100 text-gray-600">
                          {sponsors.length}
                        </span>
                      )}
                      {tab.key === "prizes" && prizes.length > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] bg-gray-100 text-gray-600">
                          {prizes.length}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Tab 1: Overview */}
              {activeTab === "overview" && (
                <div className="p-6 space-y-6">
                  {/* Banner Preview & Description */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-4">
                      <div>
                        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                          Description
                        </h3>
                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 text-sm text-gray-700 leading-relaxed">
                          {event.description || "No description provided."}
                        </div>
                      </div>

                      <div>
                        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                          Prize Info / Payout Instructions
                        </h3>
                        <div className="p-4 bg-blue-50/60 rounded-xl border border-blue-100 text-xs text-blue-950 leading-relaxed">
                          {event.prize_info_text || "No custom prize info text configured."}
                        </div>
                      </div>

                      {/* Important URLs */}
                      <div>
                        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                          Configured Links
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="p-3 bg-gray-50 rounded-xl border border-gray-100 text-xs">
                            <span className="text-gray-400 block mb-1">Support URL</span>
                            {event.support_url ? (
                              <a
                                href={safeUrl(event.support_url)}
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-600 hover:underline flex items-center gap-1 font-medium truncate"
                              >
                                {event.support_url} <FiExternalLink size={12} />
                              </a>
                            ) : (
                              <span className="text-gray-400">None</span>
                            )}
                          </div>

                          <div className="p-3 bg-gray-50 rounded-xl border border-gray-100 text-xs">
                            <span className="text-gray-400 block mb-1">Sponsor CTA URL</span>
                            {event.sponsor_cta_url ? (
                              <a
                                href={safeUrl(event.sponsor_cta_url)}
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-600 hover:underline flex items-center gap-1 font-medium truncate"
                              >
                                {event.sponsor_cta_url} <FiExternalLink size={12} />
                              </a>
                            ) : (
                              <span className="text-gray-400">None</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Banner Image Preview */}
                    <div>
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                        Event Banner
                      </h3>
                      {event.banner_url ? (
                        <div className="rounded-2xl border border-gray-200 overflow-hidden bg-gray-100">
                          <img
                            src={event.banner_url}
                            alt={event.name}
                            className="w-full h-48 object-cover"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                          <div className="p-3 bg-white text-xs text-gray-500">
                            <span className="text-gray-400 block text-[10px] uppercase">Banner URL</span>
                            <a
                              href={safeUrl(event.banner_url)}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 hover:underline truncate block"
                            >
                              {event.banner_url}
                            </a>
                          </div>
                        </div>
                      ) : (
                        <div className="h-48 rounded-2xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-2 text-gray-400 text-xs bg-gray-50/50">
                          <FiGift size={24} />
                          <span>No banner image set</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Technical Meta Parameters */}
                  <div className="pt-4 border-t border-gray-100">
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                      Event Configuration Specs
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                      <div>
                        <span className="text-gray-400 block mb-0.5">Start Date</span>
                        <span className="font-semibold text-gray-700">{formatDate(event.start_date)}</span>
                      </div>
                      <div>
                        <span className="text-gray-400 block mb-0.5">End Date</span>
                        <span className="font-semibold text-gray-700">{formatDate(event.end_date)}</span>
                      </div>
                      <div>
                        <span className="text-gray-400 block mb-0.5">Leaderboard Cache Limit</span>
                        <span className="font-semibold text-gray-700">
                          {(event.leaderboard_limit || 10000).toLocaleString()} users
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-400 block mb-0.5">Rank Bucket Batch Size</span>
                        <span className="font-semibold text-gray-700">{event.rank_bucket_size || 100}</span>
                      </div>
                      <div>
                        <span className="text-gray-400 block mb-0.5">App Design</span>
                        <span
                          className={`font-semibold ${event.theme_enabled ? "text-blue-700" : "text-gray-500"}`}
                          title={
                            event.theme_enabled
                              ? "The app shows this event's custom card, colors, text and terms"
                              : "The app shows its standard Live Event design"
                          }
                        >
                          {event.theme_enabled ? "Custom — On" : "Standard (Off)"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab 2: Sponsors */}
              {activeTab === "sponsors" && (
                <div className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800">Event Sponsors</h3>
                      <p className="text-xs text-gray-400">
                        Logos and partners displayed in the event header & sponsor carousel
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setEditingSponsor(null);
                        setIsSponsorModalOpen(true);
                      }}
                      className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl transition shadow-sm shadow-blue-600/20 flex items-center gap-1.5"
                    >
                      <FiPlus size={15} /> Add Sponsor
                    </button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-gray-50/60 border-b border-gray-100 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                          <th className="px-4 py-3">Sponsor</th>
                          <th className="px-4 py-3">Website</th>
                          <th className="px-4 py-3">Sort Order</th>
                          <th className="px-4 py-3">Status</th>
                          <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 text-xs">
                        {sponsorsLoading && sponsors.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                              Loading sponsors...
                            </td>
                          </tr>
                        ) : sponsors.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                              No sponsors added for this event yet.
                            </td>
                          </tr>
                        ) : (
                          sponsors.map((sponsor) => (
                            <tr key={sponsor.id} className="hover:bg-gray-50/70 transition">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  {sponsor.icon_url ? (
                                    <img
                                      src={sponsor.icon_url}
                                      alt={sponsor.name}
                                      className="w-9 h-9 rounded-lg object-contain bg-white border border-gray-200 p-1"
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                      }}
                                    />
                                  ) : (
                                    <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">
                                      <FiShield size={16} />
                                    </div>
                                  )}
                                  <span className="font-semibold text-gray-800">{sponsor.name}</span>
                                </div>
                              </td>

                              <td className="px-4 py-3">
                                {sponsor.website_url ? (
                                  <a
                                    href={safeUrl(sponsor.website_url)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-blue-600 hover:underline flex items-center gap-1"
                                  >
                                    {sponsor.website_url} <FiExternalLink size={11} />
                                  </a>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>

                              <td className="px-4 py-3 font-mono text-gray-600">{sponsor.sort_order ?? 0}</td>

                              <td className="px-4 py-3">
                                <span
                                  className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${sponsor.is_active
                                    ? "bg-green-100 text-green-700"
                                    : "bg-gray-100 text-gray-500"
                                    }`}
                                >
                                  {sponsor.is_active ? "Active" : "Inactive"}
                                </span>
                              </td>

                              <td className="px-4 py-3 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    onClick={() => {
                                      setEditingSponsor(sponsor);
                                      setIsSponsorModalOpen(true);
                                    }}
                                    className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition"
                                  >
                                    <FiEdit2 size={15} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteSponsor(sponsor)}
                                    className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50 transition"
                                  >
                                    <FiTrash2 size={15} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Tab 3: Prize Ladder (INLINE BUILDER - AUTOMATIC SAVING - NO BUTTON!) */}
              {activeTab === "prizes" && (
                <div className="p-6 space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                      <FiAward className="text-blue-600" /> Prize Pool Tiers ({prizes.length})
                    </h3>
                    <p className="text-xs text-gray-400">
                      Changes save automatically. Configure PX token rewards and custom coin rewards (e.g. USDT, ETH)
                    </p>
                  </div>

                  {/* List of Tier Cards */}
                  <div className="space-y-3">
                    {prizesLoading && prizes.length === 0 ? (
                      <div className="p-8 text-center text-gray-400 text-xs">
                        Loading prize tiers...
                      </div>
                    ) : prizes.length === 0 ? (
                      <div className="p-8 border-2 border-dashed border-gray-200 rounded-2xl text-center text-xs text-gray-400">
                        No prize tiers configured yet. Click &quot;+ Add Tier&quot; to add rank rewards.
                      </div>
                    ) : (
                      prizes.map((prize, idx) => {
                        const coinRows = coinRowsOf(prize.rewards);
                        return (
                          <div
                            key={prize.id || idx}
                            className="p-4 bg-white rounded-2xl border border-gray-200/80 shadow-sm transition space-y-2 hover:border-gray-300"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                              {/* RANK RANGE */}
                              <div className="w-full sm:w-40">
                                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                                  Rank Range
                                </label>
                                <div className="flex items-center gap-1.5">
                                  <input
                                    type="number"
                                    min="1"
                                    value={prize.rank_from ?? ""}
                                    onChange={(e) =>
                                      handleUpdatePrizeAndAutoSave(prize.id, {
                                        rank_from: e.target.value,
                                      })
                                    }
                                    onBlur={() => handlePrizeBlur(prize)}
                                    className="w-full px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 focus:bg-white focus:ring-2 focus:ring-blue-500/20"
                                    placeholder="1"
                                  />
                                  <span className="text-gray-400 text-xs">-</span>
                                  <input
                                    type="number"
                                    min="1"
                                    value={prize.rank_to ?? ""}
                                    onChange={(e) =>
                                      handleUpdatePrizeAndAutoSave(prize.id, {
                                        rank_to: e.target.value,
                                      })
                                    }
                                    onBlur={() => handlePrizeBlur(prize)}
                                    className="w-full px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 focus:bg-white focus:ring-2 focus:ring-blue-500/20"
                                    placeholder="1"
                                  />
                                </div>
                              </div>

                              {/* PX REWARD */}
                              <div className="w-full sm:w-44">
                                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                                  PX Reward
                                </label>
                                <div className="relative">
                                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[10px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                                    PX
                                  </span>
                                  <input
                                    type="number"
                                    min="0"
                                    step="any"
                                    value={pxAmountOf(prize.rewards)}
                                    onChange={(e) => handlePxAmountChange(prize, e.target.value)}
                                    onBlur={() => handlePrizeBlur(prize)}
                                    className="w-full pl-11 pr-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 focus:bg-white focus:ring-2 focus:ring-blue-500/20"
                                    placeholder="0"
                                  />
                                </div>
                              </div>

                              {/* OTHER COIN REWARDS — one row per currency */}
                              <div className="flex-1">
                                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                                  Other Coin Rewards
                                </label>

                                <div className="space-y-2">
                                  {coinRows.length === 0 ? (
                                    <p className="text-[11px] text-gray-400 italic py-1.5">
                                      Only PX token reward configured for this rank tier.
                                    </p>
                                  ) : (
                                    coinRows.map((row, coinIdx) => (
                                      <div key={coinIdx} className="flex items-center gap-2">
                                        <CurrencySelect
                                          value={row.currency}
                                          onChange={(currency) =>
                                            handleCoinRowChange(prize, coinIdx, { currency })
                                          }
                                          onBlur={() => handlePrizeBlur(prize)}
                                          options={COIN_CURRENCIES}
                                          selectClassName="w-24 px-2 py-1.5 bg-emerald-50 border border-emerald-200 rounded-xl text-xs font-bold text-emerald-800 text-center uppercase tracking-wider cursor-pointer focus:bg-white focus:ring-2 focus:ring-emerald-500/20"
                                          inputClassName="w-20 px-2 py-1.5 bg-white border border-emerald-200 rounded-xl text-xs font-bold text-emerald-800 text-center uppercase tracking-wider focus:ring-2 focus:ring-emerald-500/20"
                                        />

                                        <input
                                          type="number"
                                          min="0"
                                          step="any"
                                          value={row.amount ?? ""}
                                          onChange={(e) =>
                                            handleCoinRowChange(prize, coinIdx, {
                                              amount: e.target.value,
                                            })
                                          }
                                          onBlur={() => handlePrizeBlur(prize)}
                                          className="flex-1 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 focus:bg-white focus:ring-2 focus:ring-blue-500/20"
                                          placeholder="0"
                                        />

                                        <button
                                          type="button"
                                          onClick={() => handleRemoveCoinRow(prize, coinIdx)}
                                          className="p-1.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition"
                                          title="Remove this coin"
                                        >
                                          <FiX size={14} />
                                        </button>
                                      </div>
                                    ))
                                  )}

                                  <button
                                    type="button"
                                    onClick={() => handleAddCoinRow(prize)}
                                    className="text-[11px] font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1 transition"
                                  >
                                    <FiPlus size={12} /> Add coin
                                  </button>
                                </div>
                              </div>

                              {/* Delete Trash button */}
                              <button
                                type="button"
                                onClick={() => handleDeletePrize(prize)}
                                className="p-2 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition self-end sm:self-center"
                                title="Remove Tier"
                              >
                                <FiTrash2 size={16} />
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Add Tier button between tier cards and total reward summary */}
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleQuickAddPrize}
                      className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl transition shadow-sm shadow-blue-600/20 flex items-center gap-1.5"
                    >
                      <FiPlus size={15} /> Add Tier
                    </button>
                  </div>

                  {/* Total Rewards Summary Box */}
                  {prizes.length > 0 && (
                    <div className="p-3.5 bg-blue-50/70 border border-blue-100 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                      <span className="font-semibold text-blue-900 flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-blue-600"></span> Prize Pool Total Rewards:
                      </span>
                      <div className="flex items-center gap-2 flex-wrap">
                        {prizeTotals.length === 0 ? (
                          <span className="px-2.5 py-1 bg-white rounded-lg text-gray-500 font-bold border border-blue-200">
                            No rewards set yet
                          </span>
                        ) : (
                          prizeTotals.map(({ currency, amount }) => (
                            <span
                              key={currency}
                              className={`px-2.5 py-1 bg-white rounded-lg font-bold border border-blue-200 ${
                                isAutoCredited(currency) ? "text-blue-700" : "text-emerald-700"
                              }`}
                              title={
                                isAutoCredited(currency)
                                  ? "Credited automatically at finalize"
                                  : "Transferred by hand after finalize"
                              }
                            >
                              +{amount.toLocaleString()} {currency}
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tab 4: Participants & Points */}
              {activeTab === "ledger" && (
                <PointsLedgerTab
                  eventId={id}
                  onOpenHistory={(user) => setHistoryUser(user)}
                />
              )}

              {activeTab === "analytics" && (
                <AnalyticsTab eventId={id} onOpenHistory={(user) => setHistoryUser(user)} />
              )}

              {activeTab === "points-summary" && <PointsSummaryTab eventId={id} />}

              {activeTab === "task-activity" && <TaskActivityTab eventId={id} />}

              {activeTab === "exchange" && <ExchangeRatesTab eventId={id} />}

              {activeTab === "participants" && (
                <div className="p-6 space-y-4">
                  {/* Top Bar with Search & Recompute */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800">Event Scoring Participants</h3>
                      <p className="text-xs text-gray-400">
                        Live leaderboard standings, breakdown by activity, and manual adjustments
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="relative w-64">
                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                        <input
                          type="text"
                          placeholder="Search username or email..."
                          value={participantSearch}
                          onChange={handleParticipantSearchChange}
                          className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        />
                      </div>

                      {/* Block status filter: All / Active / Blocked */}
                      <div className="flex items-center bg-gray-100 rounded-lg p-0.5" title="Filter by block status">
                        {[
                          { value: "", label: "All" },
                          { value: "false", label: "Active" },
                          {
                            value: "true",
                            label: `Blocked${event?.blocked_count ? ` (${Number(event.blocked_count).toLocaleString()})` : ""}`,
                          },
                        ].map((f) => {
                          const active = participantBlockFilter === f.value;
                          return (
                            <button
                              key={f.value || "all"}
                              type="button"
                              onClick={() => {
                                setParticipantBlockFilter(f.value);
                                setParticipantPage(1);
                              }}
                              className={`px-3 py-1 text-xs font-medium rounded-md transition ${
                                active
                                  ? f.value === "true"
                                    ? "bg-white text-red-600 shadow-sm"
                                    : "bg-white text-gray-800 shadow-sm"
                                  : "text-gray-500 hover:text-gray-700"
                              }`}
                            >
                              {f.label}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        onClick={handleRecomputeRanks}
                        title="Force leaderboard re-rank"
                        className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-lg transition flex items-center gap-1.5"
                      >
                        <FiSliders size={13} /> Recompute Ranks
                      </button>
                    </div>
                  </div>

                  {/* Participants Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-gray-50/60 border-b border-gray-100 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                          <th className="px-4 py-3">Rank</th>
                          <th className="px-4 py-3">User</th>
                          <th className="px-4 py-3">Total Points</th>
                          <th className="px-4 py-3">Points Breakdown</th>
                          <th className="px-4 py-3">Streak & Check-in</th>
                          <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 text-xs">
                        {participantsLoading && participants.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                              Loading participants...
                            </td>
                          </tr>
                        ) : participants.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                              {participantBlockFilter === "true"
                                ? "No users are blocked from this event."
                                : "No participants found."}
                            </td>
                          </tr>
                        ) : (
                          participants.map((p, idx) => {
                              const pUserId = getUserId(p);
                              return (
                                <tr
                                  key={pUserId || idx}
                                  className={`transition ${p.is_blocked ? "bg-red-50/40 hover:bg-red-50/70" : "hover:bg-gray-50/70"}`}
                                >
                                  {/* Rank */}
                                  <td className="px-4 py-3">
                                    {p.is_blocked ? (
                                      <span
                                        title="Blocked from this event — no rank"
                                        className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-red-100 text-red-600"
                                      >
                                        Blocked
                                      </span>
                                    ) : (
                                    <span
                                      className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${p.rank === 1
                                        ? "bg-amber-100 text-amber-700"
                                        : p.rank === 2
                                          ? "bg-slate-200 text-slate-700"
                                          : p.rank === 3
                                            ? "bg-orange-100 text-orange-700"
                                            : "bg-gray-100 text-gray-600"
                                        }`}
                                    >
                                      {p.rank || "—"}
                                    </span>
                                    )}
                                  </td>

                                  {/* User Info */}
                                  <td className="px-4 py-3">
                                    <div>
                                      <p className="font-semibold text-gray-800 flex items-center gap-1.5">
                                        {canViewUsers && p.user_db_id ? (
                                          // `from` keeps "Events List" highlighted in
                                          // the sidebar; Back returns to this tab.
                                          <button
                                            type="button"
                                            onClick={() =>
                                              navigate(`/user-full-details/${p.user_db_id}`, {
                                                state: { from: "/event" },
                                              })
                                            }
                                            title="Open this user's details"
                                            className="text-left hover:text-blue-600 hover:underline underline-offset-2"
                                          >
                                            {p.username || "—"}
                                          </button>
                                        ) : (
                                          p.username || "—"
                                        )}
                                        {p.country && (
                                          <span className="text-[10px] bg-gray-100 text-gray-500 px-1 rounded">
                                            {p.country}
                                          </span>
                                        )}
                                      </p>
                                      {p.is_blocked && (
                                        <p
                                          className="text-[11px] text-red-500 max-w-[220px] truncate"
                                          title={`${p.block_reason || ""}${p.blocked_at ? ` — ${formatDate(p.blocked_at)}` : ""}`}
                                        >
                                          Reason: {p.block_reason || "—"}
                                        </p>
                                      )}
                                      <p className="text-[11px] text-gray-400">{p.email || "No email"}</p>
                                      {pUserId && (
                                        <button
                                          onClick={() => copyToClipboard(String(pUserId))}
                                          title="Click to copy user ID"
                                          className="text-[10px] font-mono text-gray-400 hover:text-blue-600 flex items-center gap-1 mt-0.5"
                                        >
                                          <span className="truncate max-w-[120px]">{String(pUserId)}</span>
                                          <FiCopy size={10} />
                                        </button>
                                      )}
                                    </div>
                                  </td>

                                  {/* Total Points */}
                                  <td className="px-4 py-3">
                                    <span className="text-sm font-bold text-gray-800">
                                      {Number(p.total_points || 0).toLocaleString()}
                                    </span>
                                    {/* Earned while blocked: not in the total until an unblock adds it. */}
                                    {p.is_blocked && Number(p.held_points) !== 0 && p.held_points != null && (
                                      <p
                                        className="text-[11px] font-semibold text-amber-600"
                                        title="Earned while blocked — not counted. Decided when you unblock."
                                      >
                                        +{Number(p.held_points).toLocaleString()} held
                                      </p>
                                    )}
                                  </td>

                                  {/* Points Breakdown */}
                                  <td className="px-4 py-3">
                                    {p.points_breakdown ? (
                                      <div className="grid grid-cols-3 gap-x-3 gap-y-0.5 text-[11px] text-gray-600">
                                        <span>
                                          Mining: <strong>{p.points_breakdown.daily_mining ?? 0}</strong>
                                        </span>
                                        <span>
                                          Check-in: <strong>{p.points_breakdown.daily_check_in ?? 0}</strong>
                                        </span>
                                        <span>
                                          Referral: <strong>{p.points_breakdown.referral ?? 0}</strong>
                                        </span>
                                        <span>
                                          Streak: <strong>{p.points_breakdown.streak_bonus ?? 0}</strong>
                                        </span>
                                        <span>
                                          Hub: <strong>{p.points_breakdown.creator_hub ?? 0}</strong>
                                        </span>
                                        <span>
                                          Task: <strong>{p.points_breakdown.task ?? 0}</strong>
                                          {p.task_completed_count ? (
                                            <span className="text-gray-400"> ({p.task_completed_count} done)</span>
                                          ) : null}
                                        </span>
                                        <span>
                                          Games: <strong>{p.points_breakdown.mini_game ?? 0}</strong>
                                        </span>
                                        <span>
                                          Other: <strong>{p.points_breakdown.other ?? 0}</strong>
                                        </span>
                                      </div>
                                    ) : (
                                      <span className="text-gray-400">—</span>
                                    )}
                                  </td>

                                  {/* Streak & Last check-in */}
                                  <td className="px-4 py-3">
                                    <p className="font-medium text-gray-700">
                                      {p.check_in_streak ? `${p.check_in_streak} Days Streak` : "0 Days"}
                                    </p>
                                    <p className="text-[11px] text-gray-400">
                                      Last: {p.last_check_in_date || "Never"}
                                    </p>
                                  </td>

                                  {/* Actions */}
                                  <td className="px-4 py-3 text-right">
                                    <div className="flex items-center justify-end gap-1.5">
                                      <button
                                        onClick={() => setHistoryUser(p)}
                                        title="Where these points came from, and when"
                                        className="px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-xs font-medium rounded-lg transition"
                                      >
                                        History
                                      </button>
                                      <button
                                        onClick={() => {
                                          setSelectedParticipant(p);
                                          setIsAdjustModalOpen(true);
                                        }}
                                        className="px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-600 text-xs font-medium rounded-lg transition"
                                      >
                                        Adjust Pts
                                      </button>
                                      {/* A completed event's standings are frozen. */}
                                      {event?.status !== "completed" && (
                                        <button
                                          onClick={() => setBlockTarget(p)}
                                          title={p.is_blocked ? "Unblock from this event" : "Block from this event"}
                                          className={`px-2.5 py-1 text-xs font-medium rounded-lg transition ${
                                            p.is_blocked
                                              ? "bg-green-50 hover:bg-green-100 text-green-600"
                                              : "bg-red-50 hover:bg-red-100 text-red-600"
                                          }`}
                                        >
                                          {p.is_blocked ? "Unblock" : "Block"}
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {participantTotalPages > 1 && (
                    <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                      <span>
                        Showing {participants.length} of {participantTotal} participants
                      </span>
                      <div className="flex items-center gap-1.5">
                        <button
                          disabled={participantPage <= 1 || participantsLoading}
                          onClick={() => setParticipantPage((p) => Math.max(1, p - 1))}
                          className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                        >
                          Previous
                        </button>
                        <span className="px-2 font-medium">
                          Page {participantPage} of {participantTotalPages}
                        </span>
                        <button
                          disabled={participantPage >= participantTotalPages || participantsLoading}
                          onClick={() => setParticipantPage((p) => Math.min(participantTotalPages, p + 1))}
                          className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tab 5: Winners & Payouts */}
              {activeTab === "winners" && (
                <div className="p-6 space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800">Prize Winners & Payout Queue</h3>
                      <p className="text-xs text-gray-400">
                        Tracks credited PX token rewards and manual USDT payout confirmations
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      {/* Filter by prize status */}
                      <select
                        value={winnerPrizeStatus}
                        onChange={(e) => {
                          setWinnerPrizeStatus(e.target.value);
                          setWinnerPage(1);
                        }}
                        className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      >
                        <option value="all">All Prize Statuses</option>
                        <option value="credited">Credited (PX Sent)</option>
                        <option value="paid">Paid (USDT Sent)</option>
                        <option value="none">None</option>
                      </select>

                      {event.status === "running" && (
                        <button
                          onClick={handleFinalizeEvent}
                          className="px-3.5 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-lg transition shadow-sm shadow-purple-600/20 flex items-center gap-1.5"
                        >
                          <FiAward size={14} /> Finalize Event
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Winners Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-gray-50/60 border-b border-gray-100 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                          <th className="px-4 py-3">Final Rank</th>
                          <th className="px-4 py-3">Winner Details</th>
                          <th className="px-4 py-3">Final Points</th>
                          <th className="px-4 py-3">Prize Rewards</th>
                          <th className="px-4 py-3">Payout Status</th>
                          <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 text-xs">
                        {winnersLoading && winners.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                              Loading winners...
                            </td>
                          </tr>
                        ) : winners.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                              {event.status !== "completed"
                                ? "Winners queue will be generated when the event is finalized."
                                : "No winners found for the selected filter."}
                            </td>
                          </tr>
                        ) : (
                          winners.map((winner, idx) => {
                            const winnerUserId = getUserId(winner);
                            const isPaid = Boolean(winner.paid_at || winner.prize_status === "paid");
                            // What is still owed by hand — not "is there USDT?",
                            // which would hide the button on a BTC-only prize
                            // and leave the winner unpayable from this screen.
                            const owedByHand = manualRewardsOf(winner);
                            return (
                              <tr key={winnerUserId || idx} className="hover:bg-gray-50/70 transition">
                                <td className="px-4 py-3">
                                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold bg-amber-100 text-amber-700">
                                    #{winner.final_rank || idx + 1}
                                  </span>
                                </td>

                                <td className="px-4 py-3">
                                  <p className="font-semibold text-gray-800">{winner.username || "—"}</p>
                                  <p className="text-[11px] text-gray-400">{winner.email}</p>
                                  {winner.zgram_id && (
                                    <p className="text-[11px] text-blue-600 font-medium font-mono">
                                      Zgram: {winner.zgram_id}
                                    </p>
                                  )}
                                </td>

                                <td className="px-4 py-3 font-bold text-gray-800">
                                  {Number(winner.total_points || 0).toLocaleString()}
                                </td>

                                <td className="px-4 py-3">
                                  <p className="font-semibold text-gray-800">{winner.prize_text}</p>
                                  <div className="flex gap-2 text-[11px] mt-0.5 flex-wrap">
                                    {rewardsOf(winner).map(({ currency, amount }) => (
                                      <span
                                        key={currency}
                                        className={`font-medium ${
                                          isAutoCredited(currency) ? "text-blue-600" : "text-emerald-600"
                                        }`}
                                      >
                                        +{amount.toLocaleString()} {currency}
                                      </span>
                                    ))}
                                  </div>
                                </td>

                                <td className="px-4 py-3">
                                  <div className="space-y-0.5">
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${isPaid
                                        ? "bg-emerald-100 text-emerald-700"
                                        : winner.prize_status === "credited"
                                          ? "bg-blue-100 text-blue-700"
                                          : "bg-gray-100 text-gray-600"
                                        }`}
                                    >
                                      {isPaid ? "Paid (Complete)" : winner.prize_status || "Credited"}
                                    </span>
                                    {winner.credited_at && (
                                      <p className="text-[10px] text-gray-400">
                                        Credited: {formatDate(winner.credited_at)}
                                      </p>
                                    )}
                                    {winner.paid_at && (
                                      <p className="text-[10px] text-emerald-600">
                                        Paid: {formatDate(winner.paid_at)}
                                      </p>
                                    )}
                                  </div>
                                </td>

                                <td className="px-4 py-3 text-right">
                                  {owedByHand.length > 0 && !isPaid && (
                                    <button
                                      onClick={() => handleMarkPaid(winner)}
                                      className="px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-lg transition border border-emerald-200"
                                      title={`Owed by hand: ${rewardsToText(owedByHand)}`}
                                    >
                                      Mark {owedByHand.map((r) => r.currency).join(" + ")} Paid
                                    </button>
                                  )}
                                  {isPaid && (
                                    <span className="text-emerald-600 flex items-center justify-end gap-1 text-xs">
                                      <FiCheck size={14} /> Settled
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {winnerTotalPages > 1 && (
                    <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                      <span>
                        Showing {winners.length} of {winnerTotal} winners
                      </span>
                      <div className="flex items-center gap-1.5">
                        <button
                          disabled={winnerPage <= 1 || winnersLoading}
                          onClick={() => setWinnerPage((p) => Math.max(1, p - 1))}
                          className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                        >
                          Previous
                        </button>
                        <span className="px-2 font-medium">
                          Page {winnerPage} of {winnerTotalPages}
                        </span>
                        <button
                          disabled={winnerPage >= winnerTotalPages || winnersLoading}
                          onClick={() => setWinnerPage((p) => Math.min(winnerTotalPages, p + 1))}
                          className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tab 6: Reward Coin Approval — shared with the standalone
                  Reward Coin Approval page, so the two cannot disagree. */}
              {activeTab === "approval" && (
                <div className="p-6 space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                      <FiCreditCard className="text-blue-600" /> Reward Coin Approval
                    </h3>
                  </div>
                  <ApprovalQueue eventId={id} eventStatus={event.status} />
                </div>
              )}

              {/* Tab 8: Activity Log — who did what, in order */}
              {activeTab === "activity" && (
                <div className="p-6">
                  <ActivityLog eventId={id} />
                </div>
              )}

              {/* Tab 7: Reconcile / Audit */}
              {activeTab === "audit" && (
                <div className="p-6 space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800">Point Ledger & Scoring Audit</h3>
                      <p className="text-xs text-gray-400">
                        Cross-checks point totals against individual point ledger logs for data consistency
                      </p>
                    </div>

                    <button
                      onClick={handleRunAudit}
                      disabled={auditLoading}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl transition shadow-sm shadow-blue-600/20 flex items-center gap-2"
                    >
                      <FiRefreshCw size={14} className={auditLoading ? "animate-spin" : ""} />
                      {auditLoading ? "Auditing..." : "Run Audit Check"}
                    </button>
                  </div>

                  {auditData ? (
                    <div className="space-y-4">
                      <div
                        className={`p-4 rounded-2xl border flex items-center gap-4 ${auditData.clean
                          ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                          : "bg-rose-50 border-rose-200 text-rose-900"
                          }`}
                      >
                        <div
                          className={`w-10 h-10 rounded-xl flex items-center justify-center ${auditData.clean ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
                            }`}
                        >
                          {auditData.clean ? <FiCheckCircle size={22} /> : <FiAlertTriangle size={22} />}
                        </div>
                        <div>
                          <p className="text-sm font-bold">
                            {auditData.clean
                              ? "Ledger Status: Completely Consistent"
                              : "Warning: Points Drift Detected"}
                          </p>
                          <p className="text-xs opacity-80 mt-0.5">
                            {auditData.clean
                              ? "All scoring participant balances match their immutable point logs."
                              : `${auditData.drift_count} participant balances show divergence from ledger transactions.`}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 text-xs">
                          <span className="text-gray-400 block mb-1">Participants Audited</span>
                          <span className="text-lg font-bold text-gray-800">
                            {auditData.audited_participants ?? 0}
                          </span>
                        </div>

                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 text-xs">
                          <span className="text-gray-400 block mb-1">Drift Count</span>
                          <span
                            className={`text-lg font-bold ${auditData.drift_count > 0 ? "text-rose-600" : "text-emerald-600"
                              }`}
                          >
                            {auditData.drift_count ?? 0}
                          </span>
                        </div>

                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 text-xs">
                          <span className="text-gray-400 block mb-1">Audit Status</span>
                          <span className="text-lg font-bold text-gray-800">
                            {auditData.clean ? "Clean" : "Needs Review"}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-12 border-2 border-dashed border-gray-200 rounded-2xl flex flex-col items-center justify-center gap-3 text-center">
                      <FiShield size={32} className="text-gray-300" />
                      <p className="text-xs text-gray-500 font-medium">
                        Click &quot;Run Audit Check&quot; to verify ledger integrity on demand.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Sponsor Modal */}
      <SponsorModal
        isOpen={isSponsorModalOpen}
        eventId={id}
        editingSponsor={editingSponsor}
        onClose={() => {
          setIsSponsorModalOpen(false);
          setEditingSponsor(null);
        }}
        onSave={handleSaveSponsor}
      />

      {/* Points history — read-only */}
      <ParticipantHistoryDrawer
        isOpen={!!historyUser}
        participant={historyUser}
        eventId={id}
        onClose={() => setHistoryUser(null)}
      />

      {/* Adjust Points Modal */}
      <AdjustPointsModal
        isOpen={isAdjustModalOpen}
        participant={selectedParticipant}
        onClose={() => {
          setIsAdjustModalOpen(false);
          setSelectedParticipant(null);
        }}
        onAdjust={handleAdjustPoints}
      />

      {/* Block / Unblock Modal */}
      <BlockUserModal
        isOpen={!!blockTarget}
        participant={blockTarget}
        eventId={id}
        onClose={() => setBlockTarget(null)}
        onSubmit={handleBlockToggle}
      />

      {/* Confirmation Modal */}
      <ConfirmActionModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        subMessage={confirmModal.subMessage}
        confirmText={confirmModal.confirmText}
        confirmColor={confirmModal.confirmColor}
        icon={confirmModal.icon}
        loading={confirmModal.loading}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.action}
      />
    </div>
  );
};

export default EventDetail;
